import React from 'react';

import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { FixedCostPriceItem } from '~/types/tours/costPrice';

import { formatAmount } from './formatAmount';

interface Props {
  prices: Array<FixedCostPriceItem>;
}

/**
 * Package options priced at a flat rate rather than by a sliding scale. A
 * package option holds one cost price per currency, so an option listed here
 * will not appear in the sliding scale table.
 */
export default function FixedPriceTable({ prices }: Props) {
  if (!prices.length) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="h4" fontWeight={600}>
        Fixed prices
      </Typography>

      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Season</TableCell>
              <TableCell>Configuration</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell align="right">Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prices.map((price) => (
              <TableRow
                key={`${price.tourConfiguration}-${price.packageConfiguration}-${price.season}-${price.currency}`}
                hover
              >
                <TableCell>{price.season}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {price.tourConfiguration}-{price.packageConfiguration}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={price.currency} />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {formatAmount(price.price)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary">
        A flat rate per season, charged regardless of how many pax are booked.
      </Typography>
    </Stack>
  );
}
